import React, { useState, useMemo, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Typography,
  Input,
  Tag,
  Empty,
  Modal,
  Card,
} from "antd";
import {
  DashboardOutlined,
  TeamOutlined,
  PlusCircleOutlined,
  MinusCircleOutlined,
  EditOutlined,
  DesktopOutlined,
  ThunderboltOutlined,
  UserOutlined,
  CustomerServiceOutlined,
  BankOutlined,
  DollarOutlined,
  ApiOutlined,
  AuditOutlined,
  ControlOutlined,
  CloudServerOutlined,
  SearchOutlined,
  QuestionCircleOutlined,
  SafetyCertificateOutlined,
  DatabaseOutlined,
  ExperimentOutlined,
  RadarChartOutlined,
  LoginOutlined,
  RightOutlined,
  InfoCircleOutlined,
  CameraOutlined,
  ArrowRightOutlined,
} from "@ant-design/icons";
import PageLayout from "../pageLayout/PageLayout.react";
import "./portalGuide.css";

import imgLoginRegister  from "./screenshots/login_register.png";
import imgDashboard      from "./screenshots/dashboard.png";
import imgGroupSummary   from "./screenshots/group_summary.png";
import imgCustomerApprovals from "./screenshots/customer_approvals.png";
import imgOnboard        from "./screenshots/onboarding.png";
import imgOffboard       from "./screenshots/offboarding.png";
import imgNmiChange      from "./screenshots/nmi_change.png";
import imgSystems        from "./screenshots/system_summary.png";
import imgDispatch       from "./screenshots/vpp_control.png";
import imgAccount        from "./screenshots/my_account.png";
import imgUserManagement from "./screenshots/user_management.png";
import imgTickets        from "./screenshots/tickets.png";
import imgCompany        from "./screenshots/company.png";
import imgSettlements    from "./screenshots/settlements.png";
import imgApi            from "./screenshots/api_access.png";
import imgRoles          from "./screenshots/roles_permissions.png";
import imgConfiguration  from "./screenshots/configuration.png";
import imgAuditLog       from "./screenshots/audit_log.png";
import imgEngineStatus   from "./screenshots/engine_status.png";
import imgRedisMonitor   from "./screenshots/redis_monitor.png";
import imgBacktest       from "./screenshots/backtest.png";
import imgAemo           from "./screenshots/aemo.png";

const SCREENSHOTS = {
  loginRegister:  imgLoginRegister,
  dashboard:      imgDashboard,
  groupSummary:   imgGroupSummary,
  customerApprovals: imgCustomerApprovals,
  onboard:        imgOnboard,
  offboard:       imgOffboard,
  nmiChange:      imgNmiChange,
  systems:        imgSystems,
  dispatch:       imgDispatch,
  account:        imgAccount,
  userManagement: imgUserManagement,
  tickets:        imgTickets,
  company:        imgCompany,
  settlements:    imgSettlements,
  api:            imgApi,
  roles:          imgRoles,
  configuration:  imgConfiguration,
  auditLog:       imgAuditLog,
  engineStatus:   imgEngineStatus,
  redisMonitor:   imgRedisMonitor,
  backtest:       imgBacktest,
  aemoSettlement: imgAemo,
};

const { Title, Text, Paragraph } = Typography;

// ---------------------------------------------------------------------------
// GUIDE DATA
// ---------------------------------------------------------------------------

const GUIDE_ITEMS = [
  // ── Login & Registration ──────────────────────────────────────────────────
  {
    id: "loginRegister",
    icon: <LoginOutlined />,
    color: "#1c3557",
    title: "Login & Registration",
    description: "How to sign in to the Powow VPP portal or register a new account.",
    requiredPermissions: [],
    requiredRoleTypes: null,
    sections: [
      {
        type: "intro",
        content:
          "The Powow VPP Portal is accessed via your organisation's unique portal URL. You can either sign in with an existing account or register for a new one if you have been invited by an administrator.",
      },
      {
        type: "stages",
        stages: ["Enter Credentials", "Verify Identity", "Access Granted"],
      },
      {
        type: "options",
        title: "Choose Your Path",
        options: [
          {
            title: "Option A — I have an account",
            items: [
              "Navigate to the portal URL",
              "Enter your email address",
              "Enter your password",
              "Click Sign In",
            ],
          },
          {
            title: "Option B — I need to register",
            items: [
              "Click Create Account on the login page",
              "Fill in your full name, email and company",
              "Create a secure password",
              "Enter the verification code sent to your email",
            ],
          },
        ],
      },
      {
        type: "steps",
        title: "Registration Flow — Step by Step",
        steps: [
          "Navigate to the portal URL provided by your administrator.",
          "Click Create Account on the login page.",
          "Fill in your full name, email address, and company name.",
          "Create a secure password — minimum 8 characters, one uppercase letter, one number.",
          "Click Register. A 6-digit verification code is sent to your email address.",
          "Enter the verification code on the next screen within 10 minutes.",
          "Your account is now pending administrator approval. You will receive a confirmation email when access is granted.",
        ],
      },
      {
        type: "steps",
        title: "Login Flow — Step by Step",
        steps: [
          "Go to the portal URL in your browser.",
          "Enter your registered email address.",
          "Enter your password and click Sign In.",
          "If prompted, complete any two-factor verification step.",
          "You are now on the Dashboard. Your role and accessible features are shown in the left sidebar.",
        ],
      },
      {
        type: "callout",
        content:
          "If you do not receive the verification email within 5 minutes, check your spam or junk folder. Contact your administrator if the issue persists.",
      },
      {
        type: "callout",
        content:
          "Forgot your password? Click the Forgot Password link on the login page and enter your email address. A reset link will be sent to you.",
      },
      {
        type: "screenshot",
        src: SCREENSHOTS.loginRegister,
        caption: "Login screen — enter your email and password to access the portal",
      },
    ],
  },

  // ── Dashboard ─────────────────────────────────────────────────────────────
  {
    id: "dashboard",
    icon: <DashboardOutlined />,
    color: "#1890ff",
    title: "Dashboard",
    description: "Get a quick snapshot of system performance, energy output, and key metrics at a glance.",
    requiredPermissions: ["dashboard:read"],
    requiredRoleTypes: null,
    steps: [
      "Click Dashboard in the left sidebar to open the overview page.",
      "The top row shows summary cards: total systems, energy generated, and revenue.",
      "Use the date range selector (top-right) to switch between daily, weekly, or monthly views.",
      "Scroll down to see charts showing trends over time.",
      "Click any summary card to see a filtered breakdown of that metric.",
    ],
    tip: "The dashboard auto-refreshes every few minutes — you don't need to reload the page manually.",
  },

  // ── Group Summary ─────────────────────────────────────────────────────────
  {
    id: "groupSummary",
    icon: <TeamOutlined />,
    color: "#722ed1",
    title: "Group Summary",
    description: "View and manage all your Virtual Power Plant (VPP) groups and dispatch groups in one place.",
    requiredPermissions: ["viewgroup:read", "dispatchgroup:read"],
    requiredRoleTypes: null,
    steps: [
      "Go to Group Management → Group Summary in the sidebar.",
      "A table lists all groups with their name, type, and current status.",
      "Use the search bar at the top to filter groups by name or ID.",
      "Click a group row to open its detail page with member systems and performance data.",
      "Use the filter buttons to switch between VPP groups and dispatch groups.",
    ],
    tip: "Pinned groups appear at the top of the list for quick access.",
  },

  // ── Customer Approvals ────────────────────────────────────────────────────
  {
    id: "customerApprovals",
    icon: <CustomerServiceOutlined />,
    color: "#13c2c2",
    title: "Customer Approvals",
    description:
      "Review customers who signed up in the Powow app and chose your company — approve them to take them on.",
    requiredPermissions: ["binding:read"],
    requiredRoleTypes: null,
    sections: [
      {
        type: "intro",
        content:
          "To get customers into the VPP faster — and claiming their rebate sooner — customers can now register themselves in the Powow app instead of waiting on paperwork. During sign-up they select the partner they bought their system from. For this to work, that partner needs to confirm the customer here on the platform. Until you confirm them, the customer is not linked to your company and cannot go any further.",
      },
      {
        type: "stages",
        stages: ["Customer registers in app", "Chooses a partner", "Pending approval", "Approved & linked"],
      },
      {
        type: "steps",
        title: "Reviewing a request — step by step",
        steps: [
          "Go to Group Management → Customer Approvals in the sidebar.",
          "The Pending tab lists everyone waiting on a decision. Approved and Rejected tabs show past decisions.",
          "Check the customer's name, email, phone and NMI against your own records to confirm they really are your customer.",
          "Use the search box to find someone by name, email or NMI if the list is long.",
          "Click Review on their row.",
          "Choose Approve or Reject, and optionally add a note — the note is kept in the audit trail, the customer never sees it.",
          "Confirm. The list refreshes and the customer moves to the Approved or Rejected tab.",
        ],
      },
      {
        type: "options",
        title: "What each decision does",
        options: [
          {
            title: "Approve",
            items: [
              "The customer is linked to your company",
              "Their app is notified so they can carry on",
              "They can now submit their system details in the app",
              "They appear from then on as one of your customers",
            ],
          },
          {
            title: "Reject",
            items: [
              "The customer stays unattached to any company",
              "No notification is sent to their app",
              "They can pick a partner again from the app and try once more",
              "The decision and your note are recorded in the audit trail",
            ],
          },
        ],
      },
      {
        type: "callout",
        content:
          "You only ever see requests that named your own company. Powow staff can see every partner's requests and can action one on a partner's behalf when asked — that is recorded as acting on their behalf.",
      },
      {
        type: "callout",
        content:
          "Read-only roles (such as Partner Viewer) can open this page and see the queue, but the Review button stays greyed out — approving needs the Approve Customer Bindings permission.",
      },
    ],
  },

  // ── Onboarding ────────────────────────────────────────────────────────────
  {
    id: "onboard",
    icon: <PlusCircleOutlined />,
    color: "#52c41a",
    title: "Onboarding a System",
    description: "Add a new energy system to the platform — single system or batch — through the approval pipeline.",
    requiredPermissions: ["system:onboard"],
    requiredRoleTypes: null,
    sections: [
      {
        type: "intro",
        content:
          "Onboarding connects a physical energy system (battery, solar inverter, or combined unit) to the Powow VPP platform. All onboarding requests go through an approval pipeline — a partner admin reviews the request first, then a superadmin either approves or declines it.",
      },
      {
        type: "stages",
        stages: ["Enter Details", "Pipeline Review", "Partner Admin", "Superadmin Approval"],
      },
      {
        type: "options",
        title: "Choose Your Onboarding Method",
        options: [
          {
            title: "Single System Onboard",
            items: [
              "Go to Group Management → Onboard",
              "Enter the NMI and Serial Number directly",
              "Duplicates are detected and flagged automatically",
              "Fill in the required details and submit",
              "Request enters the pipeline for approval",
            ],
          },
          {
            title: "Batch Onboard",
            items: [
              "Go to Group Management → Onboard → Batch",
              "Download the CSV template from the page",
              "Fill in details for all systems in the template",
              "Upload the completed CSV file",
              "All systems are queued in the pipeline for approval",
            ],
          },
        ],
      },
      {
        type: "steps",
        title: "Single System Onboarding — Step by Step",
        steps: [
          "Go to Group Management → Onboard in the sidebar.",
          "Enter the system's NMI (National Meter Identifier) and Serial Number in the input fields. There is no search — you enter the values directly.",
          "Duplicate NMIs or serial numbers are automatically detected and flagged — you will be notified if a system already exists.",
          "Fill in any additional required fields such as capacity and tariff type.",
          "Select the VPP group to assign the system to.",
          "Click Submit. The request is created and enters the onboarding pipeline.",
          "You can track the status of the request in the Pipeline page.",
          "A partner admin reviews the request. Once approved at that level, it is forwarded to the superadmin.",
          "The superadmin either approves or declines the request. You will be notified of the outcome.",
        ],
      },
      {
        type: "callout",
        content:
          "For batch onboarding, download the CSV template directly from the Batch Onboard page — it contains all required columns with formatting instructions.",
      },
      {
        type: "callout",
        content:
          "The system is not live on the platform until the superadmin approves the onboarding request. You can monitor progress in the Pipeline page under Group Management.",
      },
    ],
  },

  // ── Offboarding ───────────────────────────────────────────────────────────
  {
    id: "offboard",
    icon: <MinusCircleOutlined />,
    color: "#f5222d",
    title: "Offboarding a System",
    description: "Initiate removal of a system from the platform via system details — goes through an approval pipeline.",
    requiredPermissions: ["system:offboard"],
    requiredRoleTypes: null,
    sections: [
      {
        type: "intro",
        content:
          "Offboarding is initiated from the system details page, not from the sidebar. The request then enters the offboarding pipeline and requires superadmin approval before the system is removed.",
      },
      {
        type: "stages",
        stages: ["System Details", "Request Submitted", "Partner Admin", "Superadmin Approval"],
      },
      {
        type: "steps",
        title: "How to Offboard a System",
        steps: [
          "Go to Systems → Summary or use Systems → Search to find the system you want to offboard.",
          "Click on the system to open its details page.",
          "On the right-hand side panel of the system details page, locate and click the Offboard button.",
          "Review the offboarding details shown and confirm the request.",
          "The system enters the offboarding pipeline — it remains active on the platform until the request is approved.",
          "A partner admin reviews the offboarding request first.",
          "Once the partner admin approves, the request is forwarded to the superadmin.",
          "The superadmin either approves or declines the request. You will be notified of the outcome.",
        ],
      },
      {
        type: "callout",
        content:
          "The system is not removed from the platform until the superadmin approves the offboarding request. You can track the status in the offboarding pipeline.",
      },
      {
        type: "callout",
        content:
          "If an offboarding request is declined, the system remains fully active and no changes are made.",
      },
    ],
  },

  // ── NMI / SN Change ───────────────────────────────────────────────────────
  {
    id: "nmiChange",
    icon: <EditOutlined />,
    color: "#fa8c16",
    title: "NMI / Serial Number Change",
    description: "Request a correction to a system's NMI or serial number — accessible from Group Management or System Details.",
    requiredPermissions: ["approval:create", "approval:review"],
    requiredRoleTypes: null,
    sections: [
      {
        type: "intro",
        content:
          "You can raise an NMI or serial number change request from two places: the Group Management sidebar, or directly from the system details page. All change requests go through an approval pipeline.",
      },
      {
        type: "options",
        title: "Where to Access This Feature",
        options: [
          {
            title: "Via Group Management",
            items: [
              "Go to Group Management → NMI / SN Change in the sidebar",
              "Click New Request",
              "Fill in the details and submit",
            ],
          },
          {
            title: "Via System Details",
            items: [
              "Open the system details page",
              "Find the NMI / SN Change option on the right-hand side tab",
              "Click it to open the change request form",
            ],
          },
        ],
      },
      {
        type: "steps",
        title: "Raising a Change Request",
        steps: [
          "Access the NMI / SN Change form via either Group Management or the system details page (right-hand tab).",
          "Click New Request to start a new change request.",
          "Enter the current NMI or serial number and the new correct value.",
          "Add a clear reason for the change in the notes field.",
          "Submit the request — it enters the approval pipeline.",
          "A partner admin reviews the request and can forward it to the superadmin.",
          "The superadmin approves or declines the change.",
          "You will be notified once a decision is made. The request status is visible in the change request list.",
        ],
      },
      {
        type: "callout",
        content:
          "All change requests are logged for audit purposes. You can track the status of each request in the NMI / SN Change list at any time.",
      },
    ],
  },

  // ── Systems Overview ──────────────────────────────────────────────────────
  {
    id: "systems",
    icon: <DesktopOutlined />,
    color: "#13c2c2",
    title: "Systems Overview",
    description: "Browse all energy systems in your fleet, check their status, and view performance history.",
    requiredPermissions: ["system:read", "system:protected_view"],
    requiredRoleTypes: null,
    steps: [
      "Go to Systems → Summary in the sidebar to see all systems.",
      "The table shows each system's NMI, serial number, status (Online/Offline), and group.",
      "Use the Status filter to quickly find systems that are offline or in an error state.",
      "Click a system row to open its detail page with live data, history, and group membership.",
      "Go to Systems → Search to find a specific system by NMI, address, or serial number.",
    ],
    tip: "Green status means the system is online and reporting normally. Red means it needs attention.",
  },

  // ── VPP Control & Dispatch ────────────────────────────────────────────────
  {
    id: "dispatch",
    icon: <ThunderboltOutlined />,
    color: "#faad14",
    title: "VPP Control & Dispatch",
    description: "Manage control groups, bidding, and dispatch events for your Virtual Power Plant.",
    requiredPermissions: ["dispatch:read"],
    requiredRoleTypes: null,
    sections: [
      {
        type: "intro",
        content:
          "VPP Control covers everything related to managing your Virtual Power Plant — from setting up control groups and configuring bidding rules, to monitoring live dispatch events and reviewing historical performance.",
      },
      {
        type: "steps",
        title: "Manage Control Group",
        steps: [
          "Go to VPP Control → Manage Control Group in the sidebar.",
          "This page lists all VPP control groups along with the systems assigned to each.",
          "Click any control group to open its detail view — you can see member systems, current status, and group-level settings.",
          "Use this section to manage group membership and monitor the overall health of each control group.",
        ],
      },
      {
        type: "steps",
        title: "Bidding Centre",
        steps: [
          "Go to VPP Control → Bidding Centre to expand its sub-sections.",
          "Bidding Summary shows an overview of all bids — their status, time slots, and outcomes.",
          "Bidding Rules allows you to configure the automated bidding rules that govern how your control groups participate in the energy market.",
          "Click any bid in the summary to view its full details including the systems involved and the result.",
        ],
      },
      {
        type: "steps",
        title: "Dispatch Centre",
        steps: [
          "Go to VPP Control → Dispatch Centre to expand its sub-sections.",
          "Dispatch Summary provides an overview of all dispatch events — past, current, and upcoming.",
          "Dispatch Strategy lets you configure how dispatch is executed for your control groups.",
          "Current Dispatch shows a live view of any dispatch event that is happening right now.",
          "Booked Dispatch lists all upcoming scheduled dispatch events.",
          "Dispatch Report gives you historical dispatch performance data — useful for analysis and reporting.",
          "Click any dispatch event across these views to see full details including participating systems and outcomes.",
        ],
      },
      {
        type: "callout",
        content:
          "Bidding and dispatch are time-sensitive — use the Current Dispatch and Bidding Summary views to monitor live events as they happen.",
      },
    ],
  },

  // ── My Account ────────────────────────────────────────────────────────────
  {
    id: "account",
    icon: <UserOutlined />,
    color: "#1890ff",
    title: "My Account",
    description: "View and update your personal profile, change your password, and review your account settings.",
    requiredPermissions: [],
    requiredRoleTypes: null,
    steps: [
      "Click your name or avatar in the top-right corner of any page.",
      "Select Profile from the dropdown menu.",
      "Your name, email, role, and company details are shown on this page.",
      "Click Edit Profile to update your display name or contact information.",
      "Use the Change Password section to update your password — you will need your current password.",
    ],
    tip: "Your role and permissions are set by your administrator and cannot be changed from this page.",
  },

  // ── User Management ───────────────────────────────────────────────────────
  {
    id: "userManagement",
    icon: <SafetyCertificateOutlined />,
    color: "#eb2f96",
    title: "User Management",
    description: "View all users, edit their details, and create new users with the appropriate role.",
    requiredPermissions: ["user:read"],
    requiredRoleTypes: null,
    steps: [
      "Go to Account Management → User Management in the sidebar.",
      "The user list shows all users with their name, email, role, and last login.",
      "Click on any user's name to open their profile and edit their details such as name, email, or assigned role.",
      "To create a new user, click the Create User button and fill in the form with the user's details including their name, email, and role.",
      "Role changes take effect immediately — the user will see updated access on their next page load.",
    ],
    tip: "Roles control exactly what a user can see and do across the portal — assign them carefully when creating new users.",
  },

  // ── Tickets ───────────────────────────────────────────────────────────────
  {
    id: "tickets",
    icon: <CustomerServiceOutlined />,
    color: "#722ed1",
    title: "Tickets",
    description: "Raise support requests, track issues, and communicate with the support team.",
    requiredPermissions: ["ticket:read"],
    requiredRoleTypes: null,
    steps: [
      "Go to Tickets → Summary in the sidebar to see all tickets.",
      "Click New Ticket to create a new support request.",
      "Choose a category (e.g., System Issue, Billing, General) and fill in the details.",
      "Attach screenshots or files if relevant to help the support team understand the issue.",
      "Track the ticket status: Open, In Progress, or Resolved.",
      "Click any ticket to view replies from the support team and add your own comments.",
    ],
    tip: "The more detail you provide when creating a ticket, the faster it can be resolved.",
  },

  // ── Company ───────────────────────────────────────────────────────────────
  {
    id: "company",
    icon: <BankOutlined />,
    color: "#13c2c2",
    title: "Company",
    description: "Add and manage companies, view active and pending approvals, and access company-level snapshots.",
    requiredPermissions: ["company:read"],
    requiredRoleTypes: null,
    sections: [
      {
        type: "intro",
        content:
          "The Company section lets you manage all registered companies on the platform. The list is split into two tabs — Active companies and those Pending Approval — so you can easily track new registrations awaiting sign-off.",
      },
      {
        type: "steps",
        title: "Company List",
        steps: [
          "Go to Company → Company List in the sidebar.",
          "The Active tab shows all approved and live companies with their total systems and groups.",
          "The Pending Approval tab shows companies that have been created but are awaiting review.",
          "Use the search bar to find a specific company by name or ID.",
          "Click on any company row to open its detail view — showing contact details, associated systems, view groups, and revenue sharing settings.",
          "To add a new company, click the Add Company button and fill in the required details (name, type, contact information).",
          "From a company's detail view, you can also configure revenue sharing by assigning a settlement rule.",
        ],
      },
      {
        type: "steps",
        title: "Company Snapshot",
        steps: [
          "Go to Company → Snapshot for a high-level performance view across all companies.",
          "The snapshot shows aggregated energy, revenue, and system metrics per company.",
          "Use the date filter to compare performance across different time periods.",
          "Use the export button to download the data for reporting.",
        ],
      },
      {
        type: "callout",
        content:
          "New companies start in the Pending Approval tab until reviewed. Check this tab regularly to avoid delays in onboarding new partners.",
      },
    ],
  },

  // ── Settlements ───────────────────────────────────────────────────────────
  {
    id: "settlements",
    icon: <DollarOutlined />,
    color: "#52c41a",
    title: "Settlements & Billing",
    description: "Track settlement runs, review invoices, manage receivables, and view payment history.",
    requiredPermissions: ["settlement:read"],
    requiredRoleTypes: null,
    steps: [
      "Go to Settlements in the sidebar to expand the billing section.",
      "Settlement Runs shows all completed and pending settlement cycles.",
      "Click a settlement run to see the breakdown by company and system.",
      "Receivables shows amounts owed to you; Payables shows amounts you owe.",
      "Special Journals lists manual adjustments and corrections.",
      "Use the date filters to find settlements for a specific billing period.",
    ],
    tip: "Settlements are processed on a fixed schedule — check the run date column to know when each settlement was finalised.",
  },

  // ── API Access ────────────────────────────────────────────────────────────
  {
    id: "api",
    icon: <ApiOutlined />,
    color: "#fa8c16",
    title: "API Access",
    description: "View your API credentials and learn how to connect your systems to the platform programmatically.",
    requiredPermissions: ["api:read"],
    requiredRoleTypes: null,
    steps: [
      "Go to Company → API in the sidebar.",
      "Your API key and endpoint URL are shown on this page.",
      "Click Show Key to reveal your API key (keep this secret).",
      "Use the Regenerate button to create a new API key if the current one is compromised.",
      "The page also links to the full API documentation with available endpoints and examples.",
    ],
    tip: "Never share your API key publicly. If you suspect it has been exposed, regenerate it immediately.",
  },

  // ── Roles & Permissions ───────────────────────────────────────────────────
  {
    id: "roles",
    icon: <SafetyCertificateOutlined />,
    color: "#eb2f96",
    title: "Roles & Permissions",
    description: "Create and manage user roles, define what each role can access, and assign permissions.",
    requiredPermissions: [],
    requiredRoleTypes: ["SUPERADMIN"],
    steps: [
      "Go to Roles & Permissions in the sidebar (Superadmin only).",
      "The left panel lists all existing roles (e.g., Internal, Partner, Aggregator).",
      "Click a role to see its current permissions in the right panel.",
      "Toggle individual permissions on or off using the checkboxes.",
      "Click Save Changes to apply the new permission set to all users with that role.",
      "Click New Role to create a custom role with a specific set of permissions.",
    ],
    tip: "Changes to a role apply immediately to all users assigned to that role — review carefully before saving.",
  },

  // ── Configuration ─────────────────────────────────────────────────────────
  {
    id: "configuration",
    icon: <ControlOutlined />,
    color: "#f5222d",
    title: "Configuration",
    description: "Manage the platform's equipment catalogue and VPP plan templates (Superadmin only).",
    requiredPermissions: [],
    requiredRoleTypes: ["SUPERADMIN"],
    sections: [
      {
        type: "intro",
        content:
          "Configuration is a Superadmin-only area where you manage the master catalogue of hardware equipment and the VPP plan templates offered to customers. Changes here affect what is available during onboarding and customer setup platform-wide.",
      },
      {
        type: "steps",
        title: "Equipment",
        steps: [
          "Go to Configuration → Equipment in the sidebar.",
          "The equipment list shows all registered device types — meters, inverters, batteries, and EV chargers — with their vendor, type, and current status.",
          "Active equipment is available for selection during system onboarding. Deprecated equipment is hidden from onboarding flows.",
          "Click Add Equipment to register a new device type. Fill in the name, type, vendor, and optionally a datasheet URL.",
          "Click Edit on any existing entry to update its details.",
          "Use the Deprecate button to retire equipment that is no longer in use. You can restore it later if needed.",
          "The Controller flag indicates whether a device can act as the system controller during onboarding.",
        ],
      },
      {
        type: "steps",
        title: "VPP Plans",
        steps: [
          "Go to Configuration → VPP Plans in the sidebar.",
          "VPP Plans are the participation plan templates offered to customers — each plan defines revenue share, discharge thresholds, and peak bonus settings.",
          "The list shows all plans with their Revenue Share %, Min/Max Discharge (kWh), Peak Bonus %, and current Active/Inactive status.",
          "Click Add Plan to create a new plan. Fill in the plan name, description, revenue share %, peak bonus %, discharge limits, sort order, and optional availability dates.",
          "Click Edit to update an existing plan's settings.",
          "Toggle a plan's status between Active and Inactive directly from the list — inactive plans are hidden from customer-facing flows.",
        ],
      },
      {
        type: "callout",
        content:
          "Equipment and plan changes take effect immediately for new onboardings and customer setups. Existing systems and contracts are not retroactively affected.",
      },
    ],
  },

  // ── Audit Log ─────────────────────────────────────────────────────────────
  {
    id: "auditLog",
    icon: <AuditOutlined />,
    color: "#722ed1",
    title: "Audit Log",
    description: "Review a full history of all actions taken on the platform for compliance and troubleshooting.",
    requiredPermissions: [],
    requiredRoleTypes: ["SUPERADMIN"],
    steps: [
      "Go to Audit Log in the sidebar (Superadmin only).",
      "The log shows every action performed: who did it, what was changed, and when.",
      "Use the date range filter to narrow down to a specific time window.",
      "Filter by user to see all actions taken by a specific team member.",
      "Filter by action type (e.g., Login, Update, Delete) to find specific events.",
      "Export the log as a CSV for compliance reporting.",
    ],
    tip: "The audit log cannot be edited or deleted — it is a permanent, tamper-proof record.",
  },

  // ── Engine Status ─────────────────────────────────────────────────────────
  {
    id: "engineStatus",
    icon: <RadarChartOutlined />,
    color: "#13c2c2",
    title: "Engine Status",
    description: "Monitor the health of all background processing engines — realtime, settlement, bidding, dispatch, and more.",
    requiredPermissions: [],
    requiredRoleTypes: ["SUPERADMIN"],
    sections: [
      {
        type: "intro",
        content:
          "The Engine Status page shows the live health of every background processing engine that powers the platform. Each engine handles a specific domain — from real-time data ingestion to settlement calculation and bidding. Any engine going down will affect that part of the platform.",
      },
      {
        type: "steps",
        title: "How to Use Engine Status",
        steps: [
          "Go to Core Services → Engine Status in the sidebar (Superadmin only).",
          "Each engine appears as its own tab. Click a tab to view that engine's detailed status, last heartbeat, job history, and logs.",
          "A green status means the engine is running and processing normally.",
          "A red or Stopped status means the engine is not responding — investigate immediately.",
          "Check the Last Active timestamp — if it is older than a few minutes, the engine may be stuck.",
          "Use the Restart button to attempt a soft restart of a stopped or unresponsive engine.",
          "If an engine fails to restart, escalate to your system administrator.",
        ],
      },
      {
        type: "table",
        title: "Available Engines",
        headers: ["Engine", "Responsibility"],
        rows: [
          ["Realtime", "Ingests live data from systems — powers dashboards and live monitoring"],
          ["Dispatch", "Manages dispatch scheduling and execution for VPP control groups"],
          ["Settlement", "Runs financial settlement calculations and generates ledger entries"],
          ["Bidding", "Handles energy market bid submission and rule evaluation"],
          ["AEMO Settlement", "Pulls interval data from the AEMO feed for settlement processing"],
          ["Chatbot", "Powers any AI-assisted support or query features"],
          ["Clustering", "Groups systems and analyses fleet patterns"],
          ["Forecast", "Generates energy generation and demand forecasts"],
        ],
      },
      {
        type: "callout",
        content:
          "Engines handle critical platform functions — a stopped Realtime engine means live data will go stale, and a stopped Settlement engine will delay financial processing. Always investigate a red status promptly.",
      },
    ],
  },

  // ── Redis Monitor ─────────────────────────────────────────────────────────
  {
    id: "redisMonitor",
    icon: <DatabaseOutlined />,
    color: "#f5222d",
    title: "Redis Monitor",
    description: "View the health and memory usage of the caching layer that powers real-time data across the platform.",
    requiredPermissions: [],
    requiredRoleTypes: ["SUPERADMIN"],
    steps: [
      "Go to Core Services → Redis Monitor in the sidebar (Superadmin only).",
      "The page shows the current connection status to the Redis cache server.",
      "Key metrics displayed include: memory used, number of active connections, and uptime.",
      "A green status means the cache is healthy and all systems are reading/writing correctly.",
      "High memory usage (above 80%) shown in red is a warning sign — data may be evicted.",
      "If Redis is disconnected, real-time data updates across the platform will be delayed.",
      "Report a Redis outage to your system administrator — do not attempt to restart it manually.",
    ],
    tip: "Most users will never need this page day-to-day. It is primarily used during troubleshooting when live data appears stale or delayed.",
  },

  // ── Backtest ──────────────────────────────────────────────────────────────
  {
    id: "backtest",
    icon: <ExperimentOutlined />,
    color: "#722ed1",
    title: "Backtest",
    description: "Run historical simulations to evaluate how a dispatch strategy would have performed in the past.",
    requiredPermissions: [],
    requiredRoleTypes: ["SUPERADMIN"],
    steps: [
      "Go to Core Services → Backtest in the sidebar (Superadmin only).",
      "Click New Backtest to configure a simulation.",
      "Select the date range you want to simulate — this uses historical market and system data.",
      "Choose the dispatch group or strategy you want to test.",
      "Click Run — the simulation is queued and may take a few minutes depending on the date range.",
      "Once complete, the results appear in the list with a summary of estimated revenue and dispatch events.",
      "Click a completed backtest to view a detailed breakdown including charts and per-interval results.",
      "You can run multiple backtests in parallel and compare their results side by side.",
    ],
    tip: "Backtests use real historical market prices and system availability data, so results are a reliable indicator of how a strategy would have performed.",
  },

  // ── AEMO Settlement Config ────────────────────────────────────────────────
  {
    id: "aemoSettlement",
    icon: <CloudServerOutlined />,
    color: "#fa8c16",
    title: "AEMO Settlement Config",
    description: "Manage the connection settings and configuration for the Australian Energy Market Operator (AEMO) settlement service.",
    requiredPermissions: [],
    requiredRoleTypes: ["SUPERADMIN"],
    steps: [
      "Go to Core Services → AEMO Settlement in the sidebar (Superadmin only).",
      "This page shows the current connection status to the AEMO data feed.",
      "The settlement interval and data pull schedule are displayed.",
      "If the connection status shows an error, check the credentials and endpoint URL in the configuration form.",
      "Click Edit Config to update connection details such as API keys, participant IDs, or endpoint URLs.",
      "After saving changes, use the Test Connection button to verify the new settings work before the next scheduled pull.",
      "The Last Successful Sync timestamp tells you when data was last successfully retrieved from AEMO.",
    ],
    tip: "Changes to AEMO configuration should only be made when directed by your AEMO relationship manager. Incorrect settings will halt settlement data imports.",
  },
];

// ---------------------------------------------------------------------------
// HELPERS
// ---------------------------------------------------------------------------
// GUIDE GROUPS (mirrors sidebar hierarchy)
// ---------------------------------------------------------------------------

const GUIDE_GROUPS = [
  { label: "Getting Started",     ids: ["loginRegister"] },
  { label: "Dashboard",           ids: ["dashboard"] },
  { label: "Group Management",    ids: ["groupSummary", "customerApprovals", "onboard", "offboard", "nmiChange"] },
  { label: "Systems",             ids: ["systems"] },
  { label: "VPP Control",         ids: ["dispatch"] },
  { label: "Account",             ids: ["account", "userManagement"] },
  { label: "Billing",             ids: ["settlements"] },
  { label: "Support",             ids: ["tickets", "company", "api"] },
  { label: "Roles & Permissions", ids: ["roles"] },
  { label: "Administration",      ids: ["configuration", "auditLog"] },
  { label: "Core Services",       ids: ["engineStatus", "redisMonitor", "backtest", "aemoSettlement"] },
];

// ---------------------------------------------------------------------------

function filterGuideItems(items, roleType, permissions) {
  const isSuperadmin = roleType === "SUPERADMIN";
  return items.filter((item) => {
    if (isSuperadmin) return true;
    if (item.requiredRoleTypes && !item.requiredRoleTypes.includes(roleType)) return false;
    if (item.requiredPermissions && item.requiredPermissions.length > 0) {
      return item.requiredPermissions.some((p) => permissions.includes(p));
    }
    return true;
  });
}

function normaliseSections(guide) {
  const result = guide.sections
    ? [...guide.sections]
    : [
        ...(guide.steps && guide.steps.length > 0
          ? [{ type: "steps", title: "How to use it", steps: guide.steps }]
          : []),
        ...(guide.tip ? [{ type: "callout", content: guide.tip }] : []),
      ];

  // Always ensure a screenshot block exists at the end
  if (!result.some((s) => s.type === "screenshot")) {
    result.push({
      type: "screenshot",
      src: SCREENSHOTS[guide.id] || null,
      caption: `${guide.title} — portal screenshot`,
    });
  }
  return result;
}

// ---------------------------------------------------------------------------
// BLOCK RENDERERS
// ---------------------------------------------------------------------------

function ScreenshotBlock({ src, caption }) {
  const [errored, setErrored] = useState(false);

  if (!src || errored) {
    return (
      <div className="pg-screenshot-placeholder">
        <CameraOutlined className="pg-screenshot-placeholder-icon" />
        <span>Screenshot coming soon</span>
        {caption && <span className="pg-screenshot-caption">{caption}</span>}
      </div>
    );
  }

  return (
    <div className="pg-screenshot-wrap">
      <img
        src={src}
        alt={caption || "Screenshot"}
        className="pg-screenshot-img"
        onError={() => setErrored(true)}
      />
      {caption && <div className="pg-screenshot-caption">{caption}</div>}
    </div>
  );
}

function renderBlock(block, idx) {
  switch (block.type) {
    case "intro":
      return (
        <Paragraph key={idx} className="pg-block-intro">
          {block.content}
        </Paragraph>
      );

    case "stages":
      return (
        <div key={idx} className="pg-stages-wrap">
          {block.stages.map((stage, i) => (
            <React.Fragment key={i}>
              <div className="pg-stage-block">{stage}</div>
              {i < block.stages.length - 1 && (
                <RightOutlined className="pg-stage-arrow" />
              )}
            </React.Fragment>
          ))}
        </div>
      );

    case "options":
      return (
        <div key={idx} className="pg-options-wrap">
          {block.title && <div className="pg-sub-header">{block.title}</div>}
          <div className="pg-options-grid">
            {block.options.map((opt, i) => (
              <div key={i} className="pg-option-box">
                <div className="pg-option-header">{opt.title}</div>
                <ul className="pg-option-list">
                  {opt.items.map((item, j) => (
                    <li key={j}>{item}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      );

    case "steps":
      return (
        <div key={idx} className="pg-steps-wrap">
          {block.title && <div className="pg-sub-header">{block.title}</div>}
          <ol className="pg-steps-list">
            {block.steps.map((step, i) => (
              <li key={i} className="pg-step-row">
                <span className="pg-step-circle">{i + 1}</span>
                <span className="pg-step-text">{step}</span>
              </li>
            ))}
          </ol>
        </div>
      );

    case "callout":
      return (
        <div key={idx} className="pg-callout">
          <InfoCircleOutlined className="pg-callout-icon" />
          <span>{block.content}</span>
        </div>
      );

    case "table":
      return (
        <div key={idx} className="pg-table-wrap">
          {block.title && <div className="pg-sub-header">{block.title}</div>}
          <table className="pg-table">
            {block.headers && (
              <thead>
                <tr>
                  {block.headers.map((h, i) => <th key={i}>{h}</th>)}
                </tr>
              </thead>
            )}
            <tbody>
              {(block.rows || []).map((row, i) => (
                <tr key={i}>
                  {row.map((cell, j) => <td key={j}>{cell}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );

    case "screenshot":
      return <ScreenshotBlock key={idx} src={block.src} caption={block.caption} />;

    default:
      return null;
  }
}

// ---------------------------------------------------------------------------
// GUIDE CARD
// ---------------------------------------------------------------------------

function GuideCard({ guide, onClick }) {
  return (
    <Card
      className="pg-guide-card"
      onClick={() => onClick(guide)}
      hoverable
    >
      <div className="pg-card-accent" style={{ background: guide.color }} />
      <div className="pg-card-body">
        <div
          className="pg-card-icon"
          style={{ color: guide.color, background: guide.color + "18" }}
        >
          {guide.icon}
        </div>
        <div className="pg-card-content">
          <div className="pg-card-title">{guide.title}</div>
          <div className="pg-card-desc">{guide.description}</div>
        </div>
        <ArrowRightOutlined className="pg-card-arrow" style={{ color: guide.color }} />
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// GUIDE MODAL
// ---------------------------------------------------------------------------

function GuideModal({ guide, onClose }) {
  if (!guide) return null;
  const sections = normaliseSections(guide);

  return (
    <Modal
      open={!!guide}
      onCancel={onClose}
      footer={null}
      width={760}
      className="pg-modal"
      title={
        <div className="pg-modal-title">
          <span
            className="pg-modal-title-icon"
            style={{ color: guide.color, background: guide.color + "18" }}
          >
            {guide.icon}
          </span>
          <div>
            <div className="pg-modal-title-text">{guide.title}</div>
            <div className="pg-modal-title-desc">{guide.description}</div>
          </div>
        </div>
      }
      destroyOnHidden
    >
      <div className="pg-modal-body">
        {sections.map((block, idx) => renderBlock(block, idx))}
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// MAIN COMPONENT
// ---------------------------------------------------------------------------

export default function PortalGuide() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchText, setSearchText] = useState("");
  const [activeGuide, setActiveGuide] = useState(null);

  // ?guide=<id> deep link — so a single section can be linked to directly, e.g.
  // /portal-guide?guide=customerApprovals sent to a partner. Opening or closing a
  // section keeps the URL in step, so the address bar is always copy-pasteable.
  const guideParam = searchParams.get("guide");

  useEffect(() => {
    if (guideParam) {
      if (activeGuide?.id !== guideParam) {
        const match = GUIDE_ITEMS.find((g) => g.id === guideParam);
        if (match) setActiveGuide(match);
      }
    } else if (activeGuide) {
      setActiveGuide(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guideParam]);

  const openGuide = (guide) => {
    setActiveGuide(guide);
    setSearchParams(guide ? { guide: guide.id } : {}, { replace: false });
  };

  const closeGuide = () => {
    setActiveGuide(null);
    const next = new URLSearchParams(searchParams);
    next.delete("guide");
    setSearchParams(next, { replace: true });
  };

  const roleType = localStorage.getItem("roleType") || "";
  const permissions = (() => {
    try {
      return JSON.parse(localStorage.getItem("permissions") || "[]");
    } catch {
      return [];
    }
  })();

  const visibleGuides = useMemo(
    () => filterGuideItems(GUIDE_ITEMS, roleType, permissions),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [roleType, JSON.stringify(permissions)]
  );

  const filteredGuides = useMemo(() => {
    if (!searchText.trim()) return visibleGuides;
    const lower = searchText.toLowerCase();
    return visibleGuides.filter(
      (g) =>
        g.title.toLowerCase().includes(lower) ||
        g.description.toLowerCase().includes(lower)
    );
  }, [visibleGuides, searchText]);

  const roleLabelMap = {
    SUPERADMIN: { label: "Superadmin", color: "red" },
    INTERNAL: { label: "Internal", color: "blue" },
    PARTNER: { label: "Partner", color: "green" },
    AGGREGATOR: { label: "Aggregator", color: "orange" },
  };
  const roleTag = roleLabelMap[roleType] || { label: roleType || "User", color: "default" };

  return (
    <PageLayout active="portalGuide">
      <div className="pg-page">
        {/* ── PAGE HEADER ── */}
        <div className="pg-page-header">
          <div className="pg-page-header-left">
            <div className="pg-page-brand">
              <QuestionCircleOutlined className="pg-page-brand-icon" />
              <Title level={4} className="pg-page-brand-title">Portal Guide</Title>
            </div>
            <Text className="pg-page-subtitle">
              Browse guides to learn how to use each section of the portal.
            </Text>
          </div>
          <div className="pg-page-header-right">
            <Tag color={roleTag.color} className="pg-role-tag">
              {roleTag.label}
            </Tag>
            <Input
              prefix={<SearchOutlined />}
              placeholder="Search guides..."
              allowClear
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              className="pg-search-input"
            />
          </div>
        </div>

        {/* ── CARDS GRID ── */}
        {filteredGuides.length === 0 ? (
          <Empty description="No guides found" style={{ marginTop: 80 }} />
        ) : (
          <div className="pg-grouped-sections">
            {GUIDE_GROUPS.map((group) => {
              const groupGuides = group.ids
                .map((id) => filteredGuides.find((g) => g.id === id))
                .filter(Boolean);
              if (groupGuides.length === 0) return null;
              return (
                <div key={group.label} className="pg-group-section">
                  <div className="pg-group-label">{group.label}</div>
                  <div className="pg-cards-grid">
                    {groupGuides.map((guide) => (
                      <GuideCard
                        key={guide.id}
                        guide={guide}
                        onClick={openGuide}
                      />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* ── NOT FOUND NOTE ── */}
        {filteredGuides.length > 0 && (
          <div className="pg-not-found">
            <QuestionCircleOutlined className="pg-not-found-icon" />
            <span>Didn't find what you were looking for?</span>
            <button
              className="pg-not-found-link"
              onClick={() => navigate("/bug-reports/new")}
            >
              Raise a bug report
            </button>
          </div>
        )}

        {/* ── DETAIL MODAL ── */}
        <GuideModal
          guide={activeGuide}
          onClose={closeGuide}
        />
      </div>
    </PageLayout>
  );
}
